import React, { useState } from 'react';
import { ArrowRight, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import ConditionExitPopup from '../components/ConditionExitPopup';
import TrustStrip from '../components/TrustStrip';
import ClinicianCard from '../components/ClinicianCard';
import ServicesDropdown from '../components/ServicesDropdown';

const PerimenopauseLanding = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToBooking = () => {
    window.location.href = '/book';
  };

  const symptoms = [
    {
      title: 'Hot flashes and night sweats',
      description: 'You feel warm out of nowhere, wake up soaked, or dread going to bed because sleep has become unpredictable.'
    },
    {
      title: 'Brain fog and low focus',
      description: 'You lose words mid sentence, feel mentally slower, or struggle to stay on task the way you used to.'
    },
    {
      title: 'Mood shifts and anxiety',
      description: 'You feel more irritable, wired, emotional, or unlike yourself even when nothing obvious has changed.'
    },
    {
      title: 'Cycle changes',
      description: 'Your periods become heavier, lighter, closer together, farther apart, or suddenly hard to predict.'
    },
    {
      title: 'Weight and body changes',
      description: 'Your body responds differently to food, stress, and exercise, leaving you frustrated and confused.'
    },
    {
      title: 'Dryness and discomfort',
      description: 'Intimacy may feel different, your skin may change, and you may notice new irritation or sensitivity.'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Share what has changed',
      description: 'Start with your symptoms, cycle patterns, health history, and the questions you have been carrying around.'
    },
    {
      number: '2',
      title: 'Get expert evaluation',
      description: 'Your visit focuses on the whole picture so the plan matches your symptoms, goals, and medical background.'
    },
    {
      number: '3',
      title: 'Receive your care plan',
      description: 'Review hormone and non hormone options, practical lifestyle support, and the next steps that make sense for you.'
    },
    {
      number: '4',
      title: 'Adjust with follow up care',
      description: 'Your plan can evolve as symptoms shift so you are not left guessing what to do next.'
    }
  ];

  const proofPoints = [
    {
      stat: '80%',
      title: 'Experience disruptive symptoms',
      description: 'Up to 80 percent of women experience symptoms such as hot flashes, sleep changes, mood shifts, or brain fog during perimenopause.'
    },
    {
      stat: '4 wks',
      title: 'Average time to feel results',
      description: 'Most women begin noticing improvements in energy, sleep, and mood within 2 to 4 weeks of starting their personalized treatment plan.'
    },
    {
      stat: '100%',
      title: 'Online, no waiting rooms',
      description: 'All consultations happen via secure video from the comfort of your home. No traffic, no waiting rooms, no rushing.'
    }
  ];

  const faqs = [
    {
      question: 'How do I know if this is perimenopause?',
      answer: 'If you are noticing changing cycles, hot flashes, sleep problems, brain fog, mood changes, or new body changes in your late 30s, 40s, or early 50s, it may be time for a focused evaluation.'
    },
    {
      question: 'Do I have to use hormone therapy?',
      answer: 'No. Many women want to understand both hormone and non hormone options before deciding what feels right for them. We will walk you through all your options.'
    },
    {
      question: 'What makes this different from a regular visit?',
      answer: 'Our appointments are focused specifically on perimenopause symptom relief and creating a personalized plan, not a rushed general conversation where your concerns get overlooked.'
    },
    {
      question: 'How fast will I see results?',
      answer: 'Most patients begin noticing improvements in energy and sleep within 2 to 4 weeks. Full hormone optimization typically takes 2 to 3 months as we fine tune your treatment.'
    }
  ];

  return (
    <div className="peri-page">
      {/* Header */}
      <header className="peri-header">
        <div className="peri-container peri-header-inner">
          <a href="/" className="peri-brand">
            <img
              src="https://customer-assets.emergentagent.com/job_hormone-wellness-1/artifacts/emqp5y2s_symbol%20%281%29.svg"
              alt="Glow Health Logo"
              className="brand-symbol"
            />
            <span>Glow Health</span>
          </a>
          <div className="peri-header-actions">
            <ServicesDropdown testIdPrefix="peri-nav-services" />
            <button className="peri-btn peri-btn-primary" onClick={scrollToBooking}>
              Book Your Free Consultation
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="peri-hero">
          <div className="peri-container peri-hero-grid">
            <div className="peri-hero-content">
              <span className="peri-eyebrow">Perimenopause Support</span>
              <h1 className="peri-hero-headline">
                Your symptoms are real and you do not have to push through them
              </h1>
              <p className="peri-hero-sub">
                If hot flashes, brain fog, sleep trouble, anxiety, cycle changes, or low energy have started to take over your days, Glow Health offers thoughtful care built around what your body is telling you.
              </p>
              <div className="peri-hero-actions">
                <button className="peri-btn peri-btn-primary" onClick={scrollToBooking}>
                  Book a Consultation
                  <ArrowRight size={18} />
                </button>
                <a href="#symptoms" className="peri-btn peri-btn-secondary">See Common Symptoms</a>
              </div>
              <div className="peri-trust-row">
                <span className="peri-trust-pill">Personalized treatment plan</span>
                <span className="peri-trust-pill">Hormone and non hormone options</span>
                <span className="peri-trust-pill">100% virtual care</span>
              </div>
            </div>

            <div className="peri-hero-visual">
              <img
                src="https://images.unsplash.com/photo-1517365830460-955ce3ccd263?w=640&h=760&fit=crop"
                alt="A woman in perimenopause finding her footing again"
                className="peri-hero-image"
                loading="lazy"
              />
              <div className="peri-hero-image-badge">
                "I found my footing again."
              </div>
            </div>
          </div>
        </section>

        <TrustStrip />

        {/* Symptoms */}
        <section className="peri-section" id="symptoms">
          <div className="peri-container">
            <div className="peri-section-head">
              <span className="peri-eyebrow">Common Signs</span>
              <h2>Perimenopause can show up in ways many women do not expect</h2>
              <p>Most people are told to wait it out or piece together answers on their own. You deserve better than that.</p>
            </div>
            <div className="peri-symptom-grid">
              {symptoms.map((symptom, index) => (
                <div key={index} className="peri-symptom-card">
                  <h3>{symptom.title}</h3>
                  <p>{symptom.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quote */}
        <section className="peri-section peri-quote-section">
          <div className="peri-container">
            <div className="peri-quote-wrap">
              <div>
                <span className="peri-eyebrow">Why Women Choose Us</span>
                <p className="peri-quote">
                  "They want to feel heard, understood, and given a plan that actually fits their life."
                </p>
              </div>
              <div className="peri-quote-meta">
                Our care is personalized, never one size fits all. We take the time to understand your unique symptoms, history, and goals before creating your treatment plan.
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="peri-section" id="plan">
          <div className="peri-container">
            <div className="peri-section-head">
              <span className="peri-eyebrow">How Care Works</span>
              <h2>A simple path from confusion to a clear treatment plan</h2>
              <p>We reduce uncertainty by showing you exactly what happens next, what is included, and why the care feels personalized.</p>
            </div>
            <div className="peri-steps-grid">
              {steps.map((step, index) => (
                <div key={index} className="peri-step-card">
                  <div className="peri-step-number">{step.number}</div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Proof Points */}
        <section className="peri-section">
          <div className="peri-container">
            <div className="peri-section-head">
              <span className="peri-eyebrow">Results You Can Expect</span>
              <h2>Built around what women are actively searching for</h2>
            </div>
            <div className="peri-proof-grid">
              {proofPoints.map((point, index) => (
                <div key={index} className="peri-proof-card">
                  <strong>{point.stat}</strong>
                  <h3>{point.title}</h3>
                  <p>{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ClinicianCard />

        {/* CTA Section */}
        <section className="peri-section peri-cta-section" id="book">
          <div className="peri-container">
            <div className="peri-cta-wrap">
              <div className="peri-cta-content">
                <span className="peri-eyebrow">Book Now</span>
                <h2>Start with a consultation focused on your symptoms, not guesswork</h2>
                <ul className="peri-check-list">
                  <li>
                    <CheckCircle size={18} />
                    <span>Clear discussion of hot flashes, sleep changes, mood shifts, brain fog, cycle changes, and other symptoms</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Support for hormone therapy questions and non hormone care options</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>A plan built around your history, preferences, and symptom severity</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Follow up care so treatment can adjust as your body changes</span>
                  </li>
                </ul>
                <button className="peri-btn peri-btn-primary peri-btn-large" onClick={scrollToBooking}>
                  Book Your Free Consultation
                  <ArrowRight size={18} />
                </button>
                <p className="peri-cta-note">Free & confidential. No commitment required. Limited spots available.</p>
              </div>
              <div className="peri-cta-visual">
                <div className="peri-guarantee-card">
                  <h3>60 Day Guarantee</h3>
                  <p>If you do not feel meaningfully better within 60 days of starting your plan, we will work with you for free until you do. No questions asked.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="peri-section">
          <div className="peri-container">
            <div className="peri-section-head">
              <span className="peri-eyebrow">Common Questions</span>
              <h2>Questions women ask before booking</h2>
            </div>
            <div className="peri-faq-list">
              {faqs.map((faq, index) => (
                <div 
                  key={index} 
                  className={`peri-faq-item ${openFaq === index ? 'open' : ''}`}
                >
                  <button 
                    className="peri-faq-question"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span>{faq.question}</span>
                    {openFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {openFaq === index && (
                    <div className="peri-faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="peri-section peri-final-cta">
          <div className="peri-container">
            <div className="peri-final-cta-content">
              <span className="peri-eyebrow">Take The First Step</span>
              <h2>Feel more like yourself with care designed for this stage of life</h2>
              <p>You do not have to keep pushing through. Book your free consultation and start getting real answers.</p>
              <button className="peri-btn peri-btn-primary peri-btn-large" onClick={scrollToBooking}>
                Book Your Free Consultation
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="peri-footer">
        <div className="peri-container peri-footer-inner">
          <span>© {new Date().getFullYear()} Glow Health LLC</span>
          <div className="peri-footer-links">
            <a href="/privacy-policy">Privacy Policy</a>
            <span>·</span>
            <a href="/terms-of-service">Terms of Service</a>
          </div>
        </div>
      </footer>
      <ConditionExitPopup condition="perimenopause" />
    </div>
  );
};

export default PerimenopauseLanding;
